/*jslint node: true, browser: true, plusplus: true*/
"use strict";

var $ = require("pli/jquery");
var clientBroadcastManager = require("pli/clientbroadcastmanager");
var ko = require("pli/knockout");
var pli = require("pli/core");

var trackedCacheItems = {},
    stateChangedCallbacks,
    stateChangedSubscribable;

require("../jquery-extensions");

//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Class "DataCacheItem"
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-----------------------------------------------
function DataCacheItem(name) {
    /// <summary>
    /// Create new instance of DataCacheItem.
    /// Initializes properties to default values
    /// </summary>
    this.name = name;
    this.data = null;
    this.version = null;
    this.observables = [];
    this.activeCall = null;
    this.activeCallDeferred = null;
    this.error = null;
    this._handleGetCallResponseInstance = DataCacheItem.prototype._handleGetCallResponse.bind(this);
}


//-----------------------------------------------
DataCacheItem.prototype._observableDispose = function dataCacheObservableDispose() {
    /// <summary>
    /// Define logic for dispose method of observables
    /// that are stored in DataCacheItem.observables array.
    /// Expects "this" to be ko.observable.
    /// </summary>
    var trackedCacheItem = this(),
        i = ko.utils.arrayIndexOf(trackedCacheItem.observables, this);
    if (i !== -1) {
        ko.utils.arrayRemoveItem(trackedCacheItem.observables, i);
    }
};


//-----------------------------------------------
DataCacheItem.prototype._observableReload = function dataCacheObservableReload() {
    /// <summary>
    /// Re-loads the observable data.
    /// Expects "this" to be ko.observable.
    /// </summary>
    var trackedCacheItem = this();
    return trackedCacheItem.load();
};


//-----------------------------------------------
DataCacheItem.prototype._handleGetCallResponse = function dataCacheHandleGetCallResponse(response) {
    /// <summary>
    /// Always handles the response of the activeCall whether it is resolved or rejected.
    /// Expects "this" to be DataCacheItem.
    /// </summary>
    var message, i, l;
    if (!response.isError) { // replace the data in the cache
        this.data = response.result.data;
        this.version = response.result.version;
    } else { // remove the data from the cache.
        message = "Failed to load data cache entry, ".concat(this.name);
        if (window.console) { window.console.warn(message, response); }
        this.error = (response.result && response.result.message) || message;
    }

    this.activeCall = null;
    for (i = 0, l = this.observables.length; i < l; ++i) {
        this.observables[i].valueHasMutated();
    }

    if (this.error) {
        this.activeCallDeferred.reject(this);
    } else {
        this.activeCallDeferred.resolve(this);
    }
};


//-----------------------------------------------
DataCacheItem.prototype.load = function loadDataCacheItem() {
    /// <summary>
    /// Initiate Geo-DataCache-Get to pull data for
    /// this DataCacheItem unless a call is already
    /// in progress.
    /// </summary>
    var i, l;

    if (!this.activeCall) {
        this.data = null;
        this.version = null;
        this.error = null;
        this.activeCallDeferred = new $.Deferred();
        this.activeCall = (pli.global.dataCacheOptions && pli.global.dataCacheOptions.call(this.name))
            || pli.call({
                name: "Geo-DataCache-Get",
                data: { id: this.name }
            });
        this.activeCall.always(this._handleGetCallResponseInstance);

        if (this.activeCall) { // usually truthy; could be falsey if "activeCall.always" already called the handler (e.g. fatal network error) - handler will have set activeCall to null
            for (i = 0, l = this.observables.length; i < l; ++i) {
                this.observables[i].valueHasMutated();
            }
        }
    }
};


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Class "DataCache"
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-----------------------------------------------
function DataCache() {
    /// <summary>
    /// Create new instance of DataCache (singleton,
    /// one instance per browser window).
    /// </summary>
    // Do not use parent instance in IE as access to parent objects is extremely slow.
    this.parent = (document.documentMode >= 8
        ? null
        : pli.findComponent("dataCache"));
    stateChangedCallbacks = new $.Callbacks();
    stateChangedSubscribable = new $.Callbacks.Subscribable(stateChangedCallbacks);
}


//-----------------------------------------------
function findTrackedCacheItem(name) {
    /// <summary>
    /// Get instance of DataCacheItem as specified by
    /// options and stored in trackedCacheItems. If no
    /// such instance is tracked yet, creates a new
    /// instance and adds it to trackedCacheItems.
    /// </summary>
    var trackedCacheItem = trackedCacheItems[name];
    if (!trackedCacheItem) {
        trackedCacheItem = trackedCacheItems[name] = new DataCacheItem(name);
    }
    return trackedCacheItem;
}


//-----------------------------------------------
DataCache.prototype.dispose = function disposeDataCache() {
    /// <summary>
    /// Releases connections to the client broadcast
    /// manager or parent data cache instance.
    /// </summary>
    if (this._handleServerEventInstance) {
        if (this.parent) {
            this.parent.offStateChanged(this._handleServerEventInstance);
        } else if (pli.global.dataCacheOptions && pli.global.dataCacheOptions.offUpdated) {
            pli.global.dataCacheOptions.offUpdated(this._handleServerEventInstance);
        } else {
            clientBroadcastManager.offUpdated(this._handleServerEventInstance);
        }
    }
};


//-----------------------------------------------
DataCache.prototype.get = function getDataCacheItem(name) {
    /// <summary>
    /// Gets the specified data cache item.
    /// </summary>
    /// <param name="name">
    /// string that uniquely identifies the cache entry.
    /// </param>
    /// <returns type="promise">
    /// Returns jQuery's promise object (http://api.jquery.com/deferred.promise/).
    /// This object will be resolved when data is retrieved
    /// from the server or the current version of the data
    /// already exists on the client.  This object is rejected
    /// when the call to the server fails.
    /// </returns>
    this._ensureServerEventsActive();

    // forward event to parent if available.
    if (this.parent) { return this.parent.get(name); }

    var trackedCacheItem = findTrackedCacheItem(name);

    // start loading if no data (even if previous load had an error)
    // initialize activeCallDeferred if not already available
    if (!trackedCacheItem.data) { trackedCacheItem.load(); }

    return trackedCacheItem.activeCallDeferred.promise();
};


//-----------------------------------------------
DataCache.prototype.getObservable = function getDataCacheItemObservable(name) {
    /// <summary>
    /// Gets an observable for the specified data cache item.
    /// This observable always resolves to DataCacheItem
    /// instance. It will notify subscribers when anything
    /// in this instance changes (e.g. a new activeCall
    /// is started, new data was retrieved or data
    /// retrieval failed).
    /// </summary>
    /// <param name="name">
    /// string that uniquely identifies the cache entry.
    /// </param>
    /// <remarks>
    /// Returned observable contains a "dispose" method
    /// that should be called when the observable should
    /// not notify subscribers anymore.
    /// </remarks>

    this._ensureServerEventsActive();

    // forward event to parent if available.
    if (this.parent) { return this.parent.getObservable(name); }

    var trackedCacheItem = findTrackedCacheItem(name),
        o = ko.observable(trackedCacheItem);

    o.dispose = DataCacheItem.prototype._observableDispose;
    o.reload = DataCacheItem.prototype._observableReload;
    trackedCacheItem.observables.push(o);

    // start loading only if no data and previous load didn't have an error (i.e. this is a newly created trackedCacheItem)
    if (!trackedCacheItem.data && !trackedCacheItem.error) { trackedCacheItem.load(); }

    return o;
};


//-----------------------------------------------
DataCache.prototype.offStateChanged = function offStateChangedDataCache(callback) {
    /// <summary>
    /// Remove an existing callback from state changed event.
    /// </summary>
    stateChangedSubscribable.remove(callback);
};


//-----------------------------------------------
DataCache.prototype.onStateChanged = function onStateChanged(callback) {
    /// <summary>
    /// Add a callback that is fired when the state of any data cache
    /// item changes.
    /// </summary>
    stateChangedSubscribable.add(callback);
};


//-----------------------------------------------
DataCache.prototype._ensureServerEventsActive = function ensureServerEventsActiveDataCache() {
    /// <summary>
    /// Defers starting the clientBroadcastManager until the first item is requested.
    /// This function is intended to be called from get and getObservable.
    /// </summary>
    if (!this._handleServerEventInstance) {
        this._handleServerEventInstance = DataCache.prototype._handleServerEvent.bind(this);
        if (this.parent) {
            this.parent.onStateChanged(this._handleServerEventInstance);
        } else if (pli.global.dataCacheOptions && pli.global.dataCacheOptions.offUpdated) {
            pli.global.dataCacheOptions.onUpdated(this._handleServerEventInstance);
        } else {
            clientBroadcastManager.onUpdated(this._handleServerEventInstance);
        }
        $(window).unload(DataCache.prototype.dispose.bind(this));
    }
};


//-----------------------------------------------
DataCache.prototype._handleServerEvent = function handleServerDataCacheEvent(message) {
    /// <summary>
    /// Handles server events. Ignores non-dataCache
    /// events. For each dataCache event will trigger
    /// DataCache.events.stateChanged.
    /// </summary>

    if (this.parent) { // if parent forward event from parent.
        stateChangedCallbacks.fire(message);
        return;
    }

    if (message.type === "dataCache") {
        var trackedCacheItem = trackedCacheItems[message.id];
        if (trackedCacheItem) {

            // Apply server-side state to client cache item
            trackedCacheItem.previousState = message.data.previousState;
            trackedCacheItem.currentState = message.data.currentState;

            if (trackedCacheItem.data && message.data.previousState === "Loaded") { // if the data was invalidated remove from local cache (and abort active call if re-pulling data for observables)
                trackedCacheItem.data = null;
            }
            if (trackedCacheItem.data === null && trackedCacheItem.observables.length) { // if we have no data and we have observables, initiate data get now
                trackedCacheItem.load(false); // do not return promise object
            }
            stateChangedCallbacks.fire(trackedCacheItem);
        }
    }
};


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Initialization
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//------------------------------------------
module.exports = DataCache;



//////////////////
// WEBPACK FOOTER
// ./app/datacache/dataCache.js
// module id = 489
// module chunks = 49